/** Payroll: the firm's payroll desk, change queue and filings tracker, and the client's Payroll tab. */
import { formatCents, FREQUENCY_CHANGE_NOTES, PAYROLL_CHANGE_KINDS, parseDollars } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, CircleAlert, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { ask } from "@/components/dialogs";
import { DocumentPicker } from "@/components/document-picker";
import { Empty, ListSearch, Loading, PageHeader, Pager, Section, ShowMore, StatRow, usePaged, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate, localToday, plural } from "@/lib/format";
import { useMemberNames, useMembers } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import type { Doc } from "@/lib/types";
import { cn } from "@/lib/utils";

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const pill = "rounded-full px-2.5 py-0.5 text-xs font-medium";
const tone = {
  wait: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  late: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200",
  done: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
  info: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200",
  off: "bg-muted text-muted-foreground",
};
const PROVIDERS: Record<string, string> = { gusto: "Gusto", quickbooks: "QuickBooks Payroll", other: "Other provider" };
const FREQUENCY: Record<string, string> = { weekly: "Weekly", biweekly: "Every other week", semimonthly: "Twice a month", monthly: "Monthly" };

export type Run = {
  id: string;
  clientId: string;
  clientName?: string;
  scheduleId: string;
  scheduleName?: string;
  clientApproves?: boolean;
  payDate: string;
  cutoffOn: string;
  status: "upcoming" | "hours_in" | "ready" | "approved" | "processed" | "skipped";
  hoursNote: string;
  hoursDocumentId: string | null;
  grossCents: number | null;
  netCents: number | null;
  employerTaxesCents: number | null;
  federalTaxCents?: number | null;
  futaCents?: number | null;
  headcount: number | null;
  notes: string;
};
type Change = { id: string; clientId: string; clientName?: string; kind: string; employeeName: string; effectiveOn: string | null; details: string; state: string | null; checklist: Array<{ key: string; label: string; dueOn: string | null; done: boolean; doneAt: string | null }>; status: "open" | "in_progress" | "done" | "cancelled"; fromClient: boolean; assignedUserId: string | null; createdAt: string };
type Filing = { id: string; clientId: string; clientName?: string; ruleKey: string; period: string; periodLabel: string; dueOn: string; status: "due" | "filed" | "not_required"; filedOn: string | null; confirmation: string; documentId: string | null; sharedWithClient: boolean; notes: string; amountCents?: number | null; form: string; title: string; agency: string; jurisdiction: string; note: string | null; source: string };

export function runStatus(r: Run, today: string): [string, string] {
  if (r.status === "processed") return ["Processed", tone.done];
  if (r.status === "skipped") return ["Skipped", tone.off];
  if (r.cutoffOn < today) return ["Past cutoff", tone.late];
  if (r.status === "approved") return ["Approved by client", tone.info];
  if (r.status === "ready") return ["Waiting for client approval", tone.wait];
  if (r.status === "hours_in") return ["Hours received", tone.info];
  return ["Waiting for hours", tone.off];
}
const filingStatus = (f: Filing, today: string): [string, string] =>
  f.status === "filed" ? [`Filed ${formatDate(f.filedOn)}`, tone.done] : f.status === "not_required" ? ["Not required", tone.off] : f.dueOn < today ? ["Overdue", tone.late] : daysBetween(today, f.dueOn) <= 7 ? [daysBetween(today, f.dueOn) === 0 ? "Due today" : `Due in ${plural(daysBetween(today, f.dueOn), "day")}`, tone.wait] : ["Due", tone.off];

// --- Run actions ------------------------------------------------------------------------------

function RunDialog({ run, onClose }: { run: Run; onClose: () => void }) {
  const money = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2));
  const [v, setV] = useState({ gross: money(run.grossCents), net: money(run.netCents), employer: money(run.employerTaxesCents), federal: money(run.federalTaxCents ?? null), futa: money(run.futaCents ?? null), headcount: run.headcount === null ? "" : String(run.headcount), notes: run.notes, override: false });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: (status: "ready" | "processed") => {
      const c = (s: string) => (s.trim() ? parseDollars(s) : null);
      return api.send("PATCH", `/api/payroll-runs/${run.id}`, { status, grossCents: c(v.gross), netCents: c(v.net), employerTaxesCents: c(v.employer), federalTaxCents: c(v.federal), futaCents: c(v.futa), headcount: v.headcount ? Number(v.headcount) : null, notes: v.notes, ...(status === "processed" && v.override ? { override: true } : {}) });
    },
    onSuccess: onClose,
  });
  const needsApproval = run.clientApproves !== false && run.status !== "approved";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Payroll for {formatDate(run.payDate)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {run.hoursNote && (
            <div className="rounded-md bg-muted/40 p-3">
              <p className="text-xs font-medium text-muted-foreground">Hours and changes from the client</p>
              <p className="whitespace-pre-wrap">{run.hoursNote}</p>
              {run.hoursDocumentId && (
                <a className="text-xs text-primary hover:underline" href={`/api/documents/${run.hoursDocumentId}/download`}>
                  Attached file
                </a>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {(
              [
                ["gross", "Gross pay"],
                ["net", "Net pay"],
                ["employer", "Employer taxes"],
                ["headcount", "Employees paid"],
                ["federal", "Federal taxes to deposit"],
                ["futa", "FUTA tax"],
              ] as const
            ).map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label htmlFor={`rd-${k}`}>{label}</Label>
                <Input id={`rd-${k}`} inputMode="decimal" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Federal taxes to deposit: income tax withheld plus the employee and employer Social Security and Medicare, from the provider's payroll summary. ClientFlow uses it and the FUTA tax to add next-day deposits ($100,000 or more) and to apply the $2,500 and $500 deposit rules.</p>
          <div className="space-y-1">
            <Label htmlFor="rd-notes">Notes</Label>
            <Textarea id="rd-notes" rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
          </div>
          {needsApproval && run.status !== "upcoming" && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={v.override} onChange={(e) => setV({ ...v, override: e.target.checked })} /> The client approved another way (note how)
            </label>
          )}
          {needsApproval && run.status === "upcoming" && <p className="text-muted-foreground">This client approves each payroll. Send it for approval once the totals are in, or wait for their hours first.</p>}
          {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {run.status !== "processed" && run.clientApproves !== false && run.status !== "approved" && (
              <Button variant="outline" onClick={() => save.mutate("ready")} disabled={save.isPending}>
                Send to client for approval
              </Button>
            )}
            <Button onClick={() => save.mutate("processed")} disabled={save.isPending || (needsApproval && !v.override)}>
              Mark processed
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RunRow({ r, today, canEdit, showClient }: { r: Run; today: string; canEdit: boolean; showClient?: boolean }) {
  const [open, setOpen] = useState(false);
  const skip = useMutation({ mutationFn: () => api.send("PATCH", `/api/payroll-runs/${r.id}`, { status: "skipped" }) });
  const [label, cls] = runStatus(r, today);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5">
      {!showClient && <span className="w-28 shrink-0 text-sm font-medium tabular">{formatDate(r.payDate)}</span>}
      <span className={`min-w-0 flex-1 basis-full text-sm sm:basis-0 ${showClient ? "sm:min-w-[10rem]" : ""}`}>
        {showClient ? (
          <Link href={`/clients/${r.clientId}?tab=payroll`} title={r.clientName} className="block truncate font-medium hover:underline">
            {r.clientName}
          </Link>
        ) : (
          r.scheduleName
        )}
        <span className="block text-xs text-muted-foreground">
          Submit by {formatDate(r.cutoffOn)}
          {r.grossCents !== null && ` · gross ${formatCents(r.grossCents)}`}
        </span>
      </span>
      <span className={`${pill} ${cls}`}>{label}</span>
      {canEdit && r.status !== "processed" && r.status !== "skipped" && (
        <span className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {r.status === "approved" ? "Mark processed" : "Prepare"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void ask({ title: `Skip the ${formatDate(r.payDate)} payroll?`, description: "No payroll runs on this date. A skipped payroll can't be prepared later; add notes or a new run instead.", confirmLabel: "Skip payroll", destructive: true }).then((ok) => ok && skip.mutate())}
          >
            Skip
          </Button>
        </span>
      )}
      {open && <RunDialog run={r} onClose={() => setOpen(false)} />}
    </li>
  );
}

// --- Firm-wide pages ------------------------------------------------------------------------

type Overview = { today: string; clients: number; upcoming: Run[]; missedCutoff: Run[]; awaitingHours: Run[]; awaitingApproval: Run[]; changes: Change[]; overdueChecklist: Array<{ changeId: string; clientId: string; clientName: string; employeeName: string; label: string; dueOn: string }>; filings: Filing[] };

export function PayrollOverviewPage({ me }: { me: Me }) {
  const { data, isLoading } = useQuery<Overview>({ queryKey: ["/api/payroll/overview"] });
  const canEdit = hasPermission(me, "engagements.write");
  if (isLoading) return <Loading />;
  if (!data) return <Empty>The payroll overview could not be loaded.</Empty>;
  return (
    <>
      <PageHeader title="Payroll" description={`Pay dates across ${data.clients} payroll client${data.clients === 1 ? "" : "s"} for the next two weeks, what's late, changes to make, and filings due.`} />
      <StatRow
        stats={[
          { label: "Past cutoff, not processed", value: data.missedCutoff.length, tone: data.missedCutoff.length ? "danger" : "success" },
          { label: "Waiting for client approval", value: data.awaitingApproval.length, tone: data.awaitingApproval.length ? "warning" : "default" },
          { label: "Open changes", value: data.changes.length, tone: data.overdueChecklist.length ? "warning" : "default" },
          { label: "Filings due or overdue", value: data.filings.length, tone: data.filings.some((f) => f.dueOn < data.today) ? "danger" : "default" },
        ]}
      />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] [&>*]:min-w-0">
        <Section title="Next two weeks" description="Each client's pay dates with the submission cutoff.">
          {!data.upcoming.length ? (
            <p className="text-sm text-muted-foreground">No pay dates in the next two weeks. Add pay schedules on each payroll client's Payroll tab.</p>
          ) : (
            <UpcomingRuns runs={data.upcoming} today={data.today} canEdit={canEdit} />
          )}
        </Section>
        <div className="space-y-5">
          {data.missedCutoff.length > 0 && (
            <Section title="Past cutoff" description="Not processed after the submission deadline.">
              <MissedRuns runs={data.missedCutoff} today={data.today} canEdit={canEdit} />
            </Section>
          )}
          <Section title="Changes to make" action={<Link href="/payroll/changes" className="text-xs font-medium text-primary hover:underline">{data.changes.length > 8 ? `All ${data.changes.length} changes` : "All changes"}</Link>}>
            {!data.changes.length ? (
              <p className="text-sm text-muted-foreground">No open changes.</p>
            ) : (
              <ul className="-my-2 divide-y divide-border">
                {data.changes.slice(0, 8).map((c) => (
                  <li key={c.id} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{c.employeeName}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {c.clientName} · {PAYROLL_CHANGE_KINDS[c.kind]}
                      </span>
                    </span>
                    {c.fromClient && <span className={`${pill} ${tone.info}`}>From client</span>}
                  </li>
                ))}
              </ul>
            )}
            {data.overdueChecklist.length > 0 && (
              <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-xs dark:border-red-900 dark:bg-red-950/40">
                <p className="mb-1 flex items-center gap-1 font-medium">
                  <CircleAlert className="h-3.5 w-3.5" /> New-hire steps past their legal deadline
                </p>
                <div className="space-y-0.5">
                  <ShowMore
                    items={data.overdueChecklist}
                    initial={10}
                    noun="steps"
                    render={(i) => (
                      <p key={`${i.changeId}-${i.label}`}>
                        {i.clientName} · {i.employeeName}: {i.label} (due {formatDate(i.dueOn)})
                      </p>
                    )}
                  />
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>
      {/* The longest list gets the full width, so the boxes above stay in balance. */}
      <div className="mt-5">
        <Section title="Filings due or overdue" description="Overdue, and due in the next 30 days." action={<Link href="/payroll/filings" className="text-xs font-medium text-primary hover:underline">Filings tracker</Link>}>
          {!data.filings.length ? (
            <p className="text-sm text-muted-foreground">Nothing due in the next 30 days.</p>
          ) : (
            <ul className="-my-2 divide-y divide-border">
              {data.filings.slice(0, 10).map((f) => {
                const [label, cls] = filingStatus(f, data.today);
                return (
                  <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium" title={`${f.clientName} · ${f.form}`}>
                        {f.clientName} · {f.form}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {f.periodLabel} · due {formatDate(f.dueOn)}
                      </span>
                    </span>
                    <span className={`${pill} ${cls}`}>{label}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {data.filings.length > 10 && (
            <p className="mt-4 text-sm">
              Showing 10 of {data.filings.length}.{" "}
              <Link href="/payroll/filings" className="text-primary hover:underline">
                View all {data.filings.length} in the filings tracker
              </Link>
            </p>
          )}
        </Section>
      </div>
    </>
  );
}

/** The next two weeks' pay dates, 10 at a time, under their day. */
function UpcomingRuns({ runs, today, canEdit }: { runs: Run[]; today: string; canEdit: boolean }) {
  const page = usePaged(runs);
  const byDay = new Map<string, Run[]>();
  for (const r of page.items) byDay.set(r.payDate, [...(byDay.get(r.payDate) ?? []), r]);
  return (
    <div className="-m-5">
      <div className="divide-y divide-border">
        {[...byDay.entries()].map(([d, list]) => (
          <div key={d}>
            <p className="bg-secondary/40 px-4 py-1.5 text-xs font-semibold text-muted-foreground">{formatDate(d)}</p>
            <ul className="divide-y divide-border">
              {list.map((r) => (
                <RunRow key={r.id} r={r} today={today} canEdit={canEdit} showClient />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Pager paged={page} noun="pay dates" className="px-4 pb-3" compact />
    </div>
  );
}

function MissedRuns({ runs, today, canEdit }: { runs: Run[]; today: string; canEdit: boolean }) {
  const page = usePaged(runs);
  return (
    <div className="-m-5">
      <ul className="divide-y divide-border">
        {page.items.map((r) => (
          <RunRow key={r.id} r={r} today={today} canEdit={canEdit} showClient />
        ))}
      </ul>
      <Pager paged={page} noun="pay dates" className="px-4 pb-3" compact />
    </div>
  );
}

function ChangeCard({ c, canEdit, showClient }: { c: Change; canEdit: boolean; showClient?: boolean }) {
  const names = useMemberNames();
  const members = useMembers();
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/payroll-changes/${c.id}`, body) });
  const today = localToday();
  const doneCount = c.checklist.filter((i) => i.done).length;
  return (
    <li className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {c.employeeName} · {PAYROLL_CHANGE_KINDS[c.kind]}
          </p>
          <p className="text-xs text-muted-foreground">
            {showClient && (
              <Link href={`/clients/${c.clientId}?tab=payroll`} className="hover:underline">
                {c.clientName} ·{" "}
              </Link>
            )}
            {c.effectiveOn ? `Effective ${formatDate(c.effectiveOn)}` : "No date given"}
            {c.fromClient ? " · sent by the client" : ""}
          </p>
        </div>
        <span className={`${pill} ${c.status === "done" ? tone.done : c.status === "cancelled" ? tone.off : c.status === "in_progress" ? tone.info : tone.wait}`}>{c.status === "in_progress" ? "In progress" : c.status[0]!.toUpperCase() + c.status.slice(1)}</span>
      </div>
      {c.details && <p className="mt-2 whitespace-pre-wrap text-sm">{c.details}</p>}
      {c.checklist.length > 0 && (
        <ul className="mt-3 space-y-1">
          <li className="text-xs text-muted-foreground">
            {doneCount} of {c.checklist.length} done
          </li>
          {c.checklist.map((i) => (
            <li key={i.key}>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={i.done} disabled={!canEdit || c.status === "done"} onChange={(e) => save.mutate({ item: { key: i.key, done: e.target.checked } })} />
                <span className={cn("flex-1", i.done && "text-muted-foreground line-through")}>{i.label}</span>
                {i.dueOn && <span className={cn("text-xs tabular", !i.done && i.dueOn < today ? "font-medium text-red-700 dark:text-red-300" : "text-muted-foreground")}>{formatDate(i.dueOn)}</span>}
              </label>
            </li>
          ))}
        </ul>
      )}
      {canEdit && c.status !== "done" && c.status !== "cancelled" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <select aria-label="Assigned to" className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={c.assignedUserId ?? ""} onChange={(e) => save.mutate({ assignedUserId: e.target.value || null })}>
            <option value="">Unassigned</option>
            {members
              .filter((m) => m.isActive && m.role !== "client" && m.role !== "readonly")
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
          </select>
          <span className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => void ask({ title: `Cancel this change for ${c.employeeName}?`, description: "It leaves the open list. Nothing is changed in the payroll provider.", confirmLabel: "Cancel change", destructive: true }).then((ok) => ok && save.mutate({ status: "cancelled" }))}>
            Cancel change
          </Button>
          <Button size="sm" onClick={() => save.mutate({ status: "done" })} disabled={c.checklist.some((i) => !i.done)}>
            <Check className="h-3.5 w-3.5" /> Done
          </Button>
        </div>
      )}
      {c.status !== "open" && c.assignedUserId && <p className="mt-2 text-xs text-muted-foreground">Handled by {names.get(c.assignedUserId)}</p>}
      {save.error && <p className="mt-2 text-xs text-destructive">{errorMessage(save.error)}</p>}
    </li>
  );
}

export function PayrollChangesPage({ me }: { me: Me }) {
  const [all, setAll] = useState(false);
  const sp = useServerPaged(all);
  const { data: res, isLoading } = useQuery<{ items: Change[]; total: number }>({ queryKey: [`/api/payroll/changes?status=${all ? "all" : "open"}&offset=${sp.offset}&limit=${sp.size}`], placeholderData: (prev) => prev });
  const data = res?.items;
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Payroll", href: "/payroll" }]}
        title="Payroll Changes"
        description={`New hires, terminations, raises and other changes from clients and staff: ${all ? "every change, newest first" : "the open ones, oldest first"}. New hires carry their legal checklist.`}
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show finished
          </label>
        }
      />
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{all ? "No payroll changes yet." : "No open payroll changes."}</Empty>
      ) : (
        <>
          <ul className="grid gap-4 lg:grid-cols-2">
            {data.map((c) => (
              <ChangeCard key={c.id} c={c} canEdit={hasPermission(me, "engagements.write")} showClient />
            ))}
          </ul>
          <Pager paged={sp.paged(res?.total ?? 0)} noun="changes" className="mt-4" />
        </>
      )}
    </>
  );
}

function FileDialog({ f, onClose }: { f: Filing; onClose: () => void }) {
  const { data: docs } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", f.clientId] });
  const [v, setV] = useState({ status: "filed" as "filed" | "not_required", filedOn: localToday(), confirmation: f.confirmation, documentId: f.documentId ?? "", shared: f.sharedWithClient, notes: f.notes });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", `/api/payroll-filings/${f.id}`, v.status === "filed" ? { status: "filed", filedOn: v.filedOn, confirmation: v.confirmation, documentId: v.documentId || null, sharedWithClient: v.shared, notes: v.notes } : { status: "not_required", notes: v.notes }),
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {f.form} · {f.periodLabel}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <p className="text-muted-foreground">
            {f.title}. Due {formatDate(f.dueOn)}.{f.amountCents != null && ` Amount from the recorded payrolls: ${formatCents(f.amountCents)}.`} {f.note}
          </p>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={v.status === "filed"} onChange={() => setV({ ...v, status: "filed" })} /> Filed
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={v.status === "not_required"} onChange={() => setV({ ...v, status: "not_required" })} /> Not required
            </label>
          </div>
          {v.status === "filed" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="fd-on">Filed on</Label>
                  <Input id="fd-on" type="date" value={v.filedOn} onChange={(e) => setV({ ...v, filedOn: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fd-conf">Confirmation</Label>
                  <Input id="fd-conf" value={v.confirmation} onChange={(e) => setV({ ...v, confirmation: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="fd-doc">Filed copy</Label>
                <DocumentPicker id="fd-doc" docs={docs} value={v.documentId} onChange={(id) => setV({ ...v, documentId: id })} />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={v.shared} onChange={(e) => setV({ ...v, shared: e.target.checked })} /> Show the filed copy to the client
              </label>
            </>
          )}
          <div className="space-y-1">
            <Label htmlFor="fd-notes">{v.status === "not_required" ? "Why it isn't required" : "Notes"}</Label>
            <Input id="fd-notes" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
          </div>
          {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FilingList({ rows, canEdit, showClient }: { rows: Filing[]; canEdit: boolean; showClient?: boolean }) {
  const [open, setOpen] = useState<Filing | null>(null);
  const reopen = useMutation({ mutationFn: (id: string) => api.send("PATCH", `/api/payroll-filings/${id}`, { status: "due" }) });
  const today = localToday();
  return (
    <>
      <ul className="divide-y divide-border">
        {rows.map((f) => {
          const [label, cls] = filingStatus(f, today);
          return (
            <li key={f.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5">
              <span className="w-24 shrink-0 text-xs text-muted-foreground tabular">{formatDate(f.dueOn)}</span>
              <span className="min-w-0 flex-1 basis-[calc(100%-7rem)] text-sm sm:basis-0">
                {showClient && (
                  <Link href={`/clients/${f.clientId}?tab=payroll`} className="font-medium hover:underline">
                    {f.clientName} ·{" "}
                  </Link>
                )}
                <span className="font-medium">{f.form}</span> <span className="text-muted-foreground">· {f.periodLabel}</span>
                <span className="block truncate text-xs text-muted-foreground" title={f.status === "not_required" && f.notes ? f.notes : undefined}>
                  {f.status === "not_required" && f.notes ? (
                    f.notes
                  ) : (
                    <>
                      {f.agency}
                      {f.amountCents != null && <span className="tabular"> · {formatCents(f.amountCents)}</span>}
                      {f.confirmation && ` · #${f.confirmation}`}
                    </>
                  )}
                </span>
              </span>
              <span className={`${pill} ${cls}`}>{label}</span>
              {canEdit &&
                (f.status === "due" ? (
                  <Button size="sm" variant="outline" onClick={() => setOpen(f)}>
                    Record
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => reopen.mutate(f.id)}>
                    Reopen
                  </Button>
                ))}
            </li>
          );
        })}
      </ul>
      {open && <FileDialog f={open} onClose={() => setOpen(null)} />}
    </>
  );
}

export function PayrollFilingsPage({ me }: { me: Me }) {
  const [all, setAll] = useState(false);
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");
  const term = search.trim();
  const sp = useServerPaged(`${all}|${state}|${term}`);
  const qs = new URLSearchParams({ status: all ? "all" : "due", ...(state ? { jurisdiction: state } : {}), ...(term ? { q: term } : {}), offset: String(sp.offset), limit: String(sp.size) });
  const { data, isLoading } = useQuery<{ items: Filing[]; total: number; counts: { overdue: number; week: number; later: number; filed: number }; scheduleWarnings?: Array<{ clientId: string; clientName: string; nextDayOn: string; from: string; through: string }> }>({ queryKey: [`/api/payroll/filings?${qs}`], placeholderData: (prev) => prev });
  const rows = data?.items ?? [];
  const n = data?.counts;
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Payroll", href: "/payroll" }]}
        title="Payroll Filings"
        description="Federal, Maryland, Virginia and DC payroll returns and deposits each client owes, from versioned rules with their sources. Neither Gusto nor QuickBooks shares filing status, so the firm records it here."
        actions={
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <ListSearch value={search} onChange={setSearch} placeholder="Search clients" />
            <select aria-label="Jurisdiction" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">All</option>
              <option value="federal">Federal</option>
              <option value="MD">Maryland</option>
              <option value="VA">Virginia</option>
              <option value="DC">DC</option>
            </select>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Include filed
            </label>
          </div>
        }
      />
      <StatRow
        stats={[
          { label: "Overdue", value: n?.overdue ?? 0, tone: n?.overdue ? "danger" : "success" },
          { label: "Due in 7 days", value: n?.week ?? 0, tone: "warning" },
          { label: "Due later", value: n?.later ?? 0 },
          // Filed ones are only fetched with "Include filed".
          ...(all ? [{ label: "Filed", value: n?.filed ?? 0, tone: "success" as const }] : []),
        ]}
      />
      {!!data?.scheduleWarnings?.length && (
        <div className="mb-4 space-y-1.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          {data.scheduleWarnings.map((w) => (
            <p key={w.clientId} className="flex gap-2">
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <Link href={`/clients/${w.clientId}?tab=payroll`} className="font-medium hover:underline">
                  {w.clientName}
                </Link>{" "}
                is a semiweekly depositor from {formatDate(w.from)} to {formatDate(w.through)}, after $100,000 of federal taxes on the {formatDate(w.nextDayOn)} payroll. Its federal deposits are listed after each pay date; change its Federal deposits setting to Semiweekly.
              </span>
            </p>
          ))}
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>{term ? `No filings for clients matching "${term}".` : "No filings in this view."}</Empty>
      ) : (
        <div className="overflow-hidden rounded-lg border border-card-border bg-card">
          <FilingList rows={rows} canEdit={hasPermission(me, "engagements.write")} showClient />
          <Pager paged={sp.paged(data?.total ?? 0)} noun="filings" className="px-4 pb-3" />
        </div>
      )}
    </>
  );
}

// --- The client's Payroll tab --------------------------------------------------------------------

type ClientPayroll = {
  today: string;
  profile: null | {
    provider: string;
    providerName: string;
    states: string[];
    federalDepositSchedule: "monthly" | "semiweekly";
    files944: boolean;
    mdWithholdingFrequency: string;
    vaWithholdingFrequency: string;
    dcWithholdingFrequency: string;
    employeeCount: number;
    hasContractors: boolean;
    notes: string;
  };
  depositSchedule: DepositSchedule | null;
  famli: Array<{ key: string; label: string; doneOn: string | null }> | null;
  schedules: Array<{ id: string; name: string; frequency: string; anchorPayDate: string; daysOfMonth: number[]; cutoffBusinessDays: number; clientApproves: boolean; active: boolean }>;
  runs: Run[];
  changes: Change[];
  filings: Filing[];
  /** Each state's withholding frequency and the date it took or takes effect (null: since the start). */
  frequencies: Array<{ state: "MD" | "VA" | "DC"; frequency: string; effectiveFrom: string | null }>;
  frequencyDefaultFrom: string;
  rulesReviewed: string;
};

const FREQUENCY_OPTIONS: Record<"MD" | "VA" | "DC", Array<[string, string]>> = {
  MD: [
    ["accelerated", "Accelerated (each pay date)"],
    ["monthly", "Monthly"],
    ["quarterly", "Quarterly"],
    ["annual", "Annual"],
  ],
  VA: [
    ["semiweekly", "Semiweekly (VA-15/16)"],
    ["monthly", "Monthly (VA-5)"],
    ["quarterly", "Quarterly (VA-5)"],
  ],
  DC: [
    ["monthly", "Monthly payments, quarterly FR-900Q"],
    ["quarterly", "Quarterly (FR-900Q)"],
    ["annual", "Annual (FR-900A)"],
  ],
};
const frequencyName = (st: "MD" | "VA" | "DC", f: string) => FREQUENCY_OPTIONS[st].find(([k]) => k === f)?.[1] ?? f;

/** "Monthly (VA-5) until Dec 31, 2026; Semiweekly (VA-15/16) from Jan 1, 2027": a state's frequencies over time. */
function frequencyHistory(st: "MD" | "VA" | "DC", steps: ClientPayroll["frequencies"]): string | null {
  const mine = steps.filter((x) => x.state === st);
  if (mine.length < 2) return null;
  return mine
    .map((x, i) => {
      const next = mine[i + 1];
      const name = frequencyName(st, x.frequency);
      if (!x.effectiveFrom) return `${name} until ${formatDate(dayBefore(next!.effectiveFrom!))}`;
      return next ? `${name} from ${formatDate(x.effectiveFrom)} until ${formatDate(dayBefore(next.effectiveFrom!))}` : `${name} from ${formatDate(x.effectiveFrom)}`;
    })
    .join("; ");
}
const dayBefore = (d: string) => new Date(Date.parse(`${d}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10);

type DepositSchedule = { year: number; lookbackFrom: string; lookbackTo: string; lookbackTotalCents: number | null; nextDayOn: string | null; suggested: "monthly" | "semiweekly" | null };

/** What the recorded payrolls say about the federal deposit schedule (IRS Publication 15, section 11). */
function depositScheduleHint(ds: DepositSchedule, current: "monthly" | "semiweekly"): string {
  const check = (want: string) => (want === current ? "" : ` The setting says ${current}: change it, or check the IRS notice.`);
  if (ds.nextDayOn) {
    const y = Number(ds.nextDayOn.slice(0, 4));
    return `The ${formatDate(ds.nextDayOn)} payroll carried $100,000 or more of federal taxes, so the client deposits semiweekly for the rest of ${y} and all of ${y + 1}.${check("semiweekly")}`;
  }
  if (ds.suggested && ds.lookbackTotalCents !== null)
    return `${ds.year} lookback period (${formatDate(ds.lookbackFrom)} to ${formatDate(ds.lookbackTo)}): ${formatCents(ds.lookbackTotalCents)} of federal taxes, ${ds.suggested === "monthly" ? "$50,000 or less" : "more than $50,000"}, so ${ds.year} deposits are ${ds.suggested}.${check(ds.suggested)}`;
  return `ClientFlow doesn't have every payroll for the ${ds.year} lookback period (${formatDate(ds.lookbackFrom)} to ${formatDate(ds.lookbackTo)}), so it can't check the schedule. Use the IRS notice or the payroll provider: $50,000 or less of federal taxes in that period means monthly; more means semiweekly.`;
}

type FreqKey = "mdWithholdingFrequency" | "vaWithholdingFrequency" | "dcWithholdingFrequency";
const FREQ_KEY: Record<"MD" | "VA" | "DC", FreqKey> = { MD: "mdWithholdingFrequency", VA: "vaWithholdingFrequency", DC: "dcWithholdingFrequency" };

function ProfileForm({ clientId, p, onDone, depositSchedule, frequencies = [], defaultFrom, today }: { clientId: string; p: ClientPayroll["profile"]; onDone?: () => void; depositSchedule?: DepositSchedule | null; frequencies?: ClientPayroll["frequencies"]; defaultFrom?: string; today?: string }) {
  const [v, setV] = useState({
    provider: p?.provider ?? "quickbooks",
    providerName: p?.providerName ?? "",
    states: p?.states ?? ["MD"],
    federalDepositSchedule: p?.federalDepositSchedule ?? "monthly",
    files944: p?.files944 ?? false,
    mdWithholdingFrequency: p?.mdWithholdingFrequency ?? "quarterly",
    vaWithholdingFrequency: p?.vaWithholdingFrequency ?? "quarterly",
    dcWithholdingFrequency: p?.dcWithholdingFrequency ?? "quarterly",
    employeeCount: String(p?.employeeCount ?? 0),
    hasContractors: p?.hasContractors ?? false,
  });
  // A planned change (a date still ahead) can have its date corrected; a new frequency gets a date.
  const planned = (st: "MD" | "VA" | "DC") => {
    const at = frequencies.filter((x) => x.state === st).at(-1)?.effectiveFrom ?? null;
    return at && today && at > today ? at : null;
  };
  const [from, setFrom] = useState<Record<"MD" | "VA" | "DC", string>>(() => ({ MD: planned("MD") ?? defaultFrom ?? "", VA: planned("VA") ?? defaultFrom ?? "", DC: planned("DC") ?? defaultFrom ?? "" }));
  const asksDate = (st: "MD" | "VA" | "DC", key: FreqKey) => !!p && (v[key] !== p[key] || !!planned(st));
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const dated = (["MD", "VA", "DC"] as const).filter((st) => v.states.includes(st) && asksDate(st, FREQ_KEY[st]) && from[st]);
      return api.send("PUT", `/api/clients/${clientId}/payroll/profile`, { ...v, employeeCount: Number(v.employeeCount) || 0, ...(dated.length ? { withholdingFrequencyFrom: Object.fromEntries(dated.map((st) => [st, from[st]])) } : {}) });
    },
    onSuccess: onDone,
  });
  const freq = (st: "MD" | "VA" | "DC", label: string) => {
    const key = FREQ_KEY[st];
    const history = frequencyHistory(st, frequencies);
    const dated = asksDate(st, key);
    return (
      <div className="space-y-1">
        <Label htmlFor={`pp-${key}`}>{label}</Label>
        <select id={`pp-${key}`} className={select} value={v[key]} onChange={(e) => setV({ ...v, [key]: e.target.value })}>
          {FREQUENCY_OPTIONS[st].map(([k, n]) => (
            <option key={k} value={k}>
              {n}
            </option>
          ))}
        </select>
        {history && <p className="text-xs text-muted-foreground">{history}</p>}
        {dated && (
          <div className="mt-2 space-y-1">
            <Label htmlFor={`pp-${st}-from`}>Takes effect from</Label>
            <Input id={`pp-${st}-from`} type="date" value={from[st]} onChange={(e) => setFrom({ ...from, [st]: e.target.value })} className="w-44" />
            <p className="text-xs text-muted-foreground">
              {from[st] && !from[st].endsWith("-01-01") ? FREQUENCY_CHANGE_NOTES[st] : "The earlier frequency's filings run until the day before."}
            </p>
          </div>
        )}
      </div>
    );
  };
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-3">
        <div className="space-y-1">
          <Label htmlFor="pp-prov">Payroll provider</Label>
          <select id="pp-prov" className={select} value={v.provider} onChange={(e) => setV({ ...v, provider: e.target.value })}>
            {Object.entries(PROVIDERS).map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
        </div>
        {v.provider === "other" && (
          <div className="space-y-1">
            <Label htmlFor="pp-pname">Provider name</Label>
            <Input id="pp-pname" value={v.providerName} onChange={(e) => setV({ ...v, providerName: e.target.value })} />
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="pp-emp">Employees</Label>
          <Input id="pp-emp" inputMode="numeric" value={v.employeeCount} onChange={(e) => setV({ ...v, employeeCount: e.target.value })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="text-muted-foreground">Employees work in:</span>
        {["MD", "VA", "DC"].map((st) => (
          <label key={st} className="flex items-center gap-1.5">
            <input type="checkbox" checked={v.states.includes(st)} onChange={(e) => setV({ ...v, states: e.target.checked ? [...v.states, st] : v.states.filter((x) => x !== st) })} /> {st}
          </label>
        ))}
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={v.hasContractors} onChange={(e) => setV({ ...v, hasContractors: e.target.checked })} /> Pays contractors (1099-NEC)
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={v.files944} onChange={(e) => setV({ ...v, files944: e.target.checked })} /> Files Form 944 (IRS notified)
        </label>
      </div>
      <div className="grid gap-3">
        <div className="space-y-1">
          <Label htmlFor="pp-dep">Federal deposits</Label>
          <select id="pp-dep" className={select} value={v.federalDepositSchedule} onChange={(e) => setV({ ...v, federalDepositSchedule: e.target.value as "monthly" })}>
            <option value="monthly">Monthly</option>
            <option value="semiweekly">Semiweekly</option>
          </select>
        </div>
        {v.states.includes("MD") && freq("MD", "Maryland MW506")}
        {v.states.includes("VA") && freq("VA", "Virginia withholding")}
        {v.states.includes("DC") && freq("DC", "DC withholding")}
      </div>
      {depositSchedule && <p className="text-xs text-muted-foreground">{depositScheduleHint(depositSchedule, v.federalDepositSchedule)}</p>}
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      {save.isSuccess && !onDone && <p className="text-sm text-emerald-700 dark:text-emerald-300">Saved. The filing calendar follows the new settings.</p>}
      <Button type="submit" size="sm" disabled={save.isPending}>
        {p ? "Save" : "Set up payroll"}
      </Button>
    </form>
  );
}

function ScheduleForm({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const [v, setV] = useState({ name: "", frequency: "biweekly", anchorPayDate: localToday(), days: "15, 31", cutoff: "2", approves: true });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", `/api/clients/${clientId}/payroll/schedules`, {
        name: v.name || `${v.frequency[0]!.toUpperCase()}${v.frequency.slice(1)}`,
        frequency: v.frequency,
        anchorPayDate: v.anchorPayDate,
        daysOfMonth: v.frequency === "semimonthly" || v.frequency === "monthly" ? v.days.split(",").map((x) => Number(x.trim())).filter(Boolean) : [],
        cutoffBusinessDays: Number(v.cutoff) || 0,
        clientApproves: v.approves,
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="sf-name">Name</Label>
        <Input id="sf-name" placeholder="e.g. Hourly staff" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="sf-freq">Frequency</Label>
        <select id="sf-freq" className={select} value={v.frequency} onChange={(e) => setV({ ...v, frequency: e.target.value, days: e.target.value === "monthly" ? "31" : "15, 31" })}>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every two weeks</option>
          <option value="semimonthly">Twice a month</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      {v.frequency === "weekly" || v.frequency === "biweekly" ? (
        <div className="space-y-1">
          <Label htmlFor="sf-anchor">A pay date</Label>
          <Input id="sf-anchor" type="date" value={v.anchorPayDate} onChange={(e) => setV({ ...v, anchorPayDate: e.target.value })} />
        </div>
      ) : (
        <div className="space-y-1">
          <Label htmlFor="sf-days">Days of the month (31 = last day)</Label>
          <Input id="sf-days" value={v.days} onChange={(e) => setV({ ...v, days: e.target.value })} />
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="sf-cut">Submit to the provider (business days before)</Label>
        <Input id="sf-cut" inputMode="numeric" value={v.cutoff} onChange={(e) => setV({ ...v, cutoff: e.target.value })} />
      </div>
      <label className="flex items-end gap-2 pb-2 text-sm sm:col-span-2">
        <input type="checkbox" checked={v.approves} onChange={(e) => setV({ ...v, approves: e.target.checked })} /> The client approves each payroll in the portal
      </label>
      {save.error && <p className="text-sm text-destructive sm:col-span-3">{errorMessage(save.error)}</p>}
      <div className="flex justify-end gap-2 sm:col-span-3">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={save.isPending}>
          Add schedule
        </Button>
      </div>
    </form>
  );
}

export function ChangeForm({ endpoint, clientId, onDone, kinds = PAYROLL_CHANGE_KINDS }: { endpoint: string; clientId?: string; onDone: () => void; kinds?: Record<string, string> }) {
  const [v, setV] = useState({ kind: "new_hire", employeeName: "", effectiveOn: "", details: "", state: "MD" });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", endpoint, { ...(clientId ? { clientId } : {}), kind: v.kind, employeeName: v.employeeName, effectiveOn: v.effectiveOn || null, details: v.details, state: v.kind === "new_hire" ? v.state : null }),
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="cf-kind">What's changing</Label>
        <select id="cf-kind" className={select} value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })}>
          {Object.entries(kinds).map(([k, n]) => (
            <option key={k} value={k}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="cf-name">Employee</Label>
        <Input id="cf-name" required value={v.employeeName} onChange={(e) => setV({ ...v, employeeName: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="cf-date">{v.kind === "new_hire" ? "First day of work" : v.kind === "termination" ? "Last day" : "Effective date"}</Label>
        <Input id="cf-date" type="date" required={v.kind === "new_hire"} value={v.effectiveOn} onChange={(e) => setV({ ...v, effectiveOn: e.target.value })} />
      </div>
      {v.kind === "new_hire" && (
        <div className="space-y-1">
          <Label htmlFor="cf-state">Works in</Label>
          <select id="cf-state" className={select} value={v.state} onChange={(e) => setV({ ...v, state: e.target.value })}>
            <option value="MD">Maryland</option>
            <option value="VA">Virginia</option>
            <option value="DC">District of Columbia</option>
            <option value="other">Another state</option>
          </select>
        </div>
      )}
      <div className="space-y-1 sm:col-span-2">
        <Label htmlFor="cf-details">Details</Label>
        <Textarea id="cf-details" rows={2} placeholder={v.kind === "new_hire" ? "Position, pay rate, hours" : "What changes"} value={v.details} onChange={(e) => setV({ ...v, details: e.target.value })} />
      </div>
      {save.error && <p className="text-sm text-destructive sm:col-span-2">{errorMessage(save.error)}</p>}
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          Send
        </Button>
      </div>
    </form>
  );
}

export function PayrollTab({ clientId, me }: { clientId: string; me: Me }) {
  const { data, isLoading } = useQuery<ClientPayroll>({ queryKey: [`/api/clients/${clientId}/payroll`] });
  const canEdit = hasPermission(me, "engagements.write");
  const [adding, setAdding] = useState<"schedule" | "change" | null>(null);
  const famli = useMutation({ mutationFn: (v: { key: string; done: boolean }) => api.send("PUT", `/api/clients/${clientId}/payroll/profile`, { famliDone: { [v.key]: v.done } }) });
  if (isLoading) return <Loading />;
  if (!data) return <Empty>Payroll could not be loaded.</Empty>;
  if (!data.profile)
    return (
      <Section title="Set up payroll" description="Which provider runs the payroll and where employees work; the filings tracker follows from this.">
        {canEdit ? <ProfileForm clientId={clientId} p={null} /> : <Empty>Payroll isn't set up for this client.</Empty>}
      </Section>
    );
  const upcoming = data.runs.filter((r) => r.payDate >= data.today).slice(0, 6);
  const recent = data.runs.filter((r) => r.payDate < data.today).reverse().slice(0, 6);
  return (
    <div className="space-y-6">
      <StatRow
        stats={[
          { label: "Next pay date", value: upcoming[0] ? formatDate(upcoming[0].payDate) : "—" },
          { label: "Submit by", value: upcoming[0] ? formatDate(upcoming[0].cutoffOn) : "—", tone: upcoming[0] && upcoming[0].cutoffOn <= data.today ? "warning" : "default" },
          { label: "Open changes", value: data.changes.filter((c) => c.status === "open" || c.status === "in_progress").length },
          { label: "Filings overdue", value: data.filings.filter((f) => f.status === "due" && f.dueOn < data.today).length, tone: data.filings.some((f) => f.status === "due" && f.dueOn < data.today) ? "danger" : "success" },
        ]}
      />
      <div className="space-y-6">
        <Section
          title="Pay calendar"
          description={data.schedules.map((s) => `${s.name && s.name.toLowerCase() !== s.frequency ? s.name : (FREQUENCY[s.frequency] ?? s.frequency)}${s.active ? "" : " (paused)"}`).join(" · ") || "No pay schedule yet"}
          action={
            canEdit && (
              <Button size="sm" variant="outline" onClick={() => setAdding("schedule")}>
                <Plus className="h-3.5 w-3.5" /> Schedule
              </Button>
            )
          }
        >
          {adding === "schedule" && (
            <div className="mb-4">
              <ScheduleForm clientId={clientId} onDone={() => setAdding(null)} />
            </div>
          )}
          {!data.runs.length ? (
            <p className="text-sm text-muted-foreground">Add a pay schedule to build the calendar.</p>
          ) : (
            <div className="-m-5">
              <p className="bg-secondary/40 px-4 py-1.5 text-xs font-semibold text-muted-foreground">Upcoming</p>
              <ul className="divide-y divide-border">
                {upcoming.map((r) => (
                  <RunRow key={r.id} r={{ ...r, scheduleName: data.schedules.find((s) => s.id === r.scheduleId)?.name, clientApproves: data.schedules.find((s) => s.id === r.scheduleId)?.clientApproves }} today={data.today} canEdit={canEdit} />
                ))}
              </ul>
              {recent.length > 0 && (
                <>
                  <p className="bg-secondary/40 px-4 py-1.5 text-xs font-semibold text-muted-foreground">Recent</p>
                  <ul className="divide-y divide-border">
                    {recent.map((r) => (
                      <RunRow key={r.id} r={{ ...r, scheduleName: data.schedules.find((s) => s.id === r.scheduleId)?.name, clientApproves: data.schedules.find((s) => s.id === r.scheduleId)?.clientApproves }} today={data.today} canEdit={canEdit} />
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Section>
        <Section
          title="Changes and new hires"
          action={
            canEdit && (
              <Button size="sm" variant="outline" onClick={() => setAdding("change")}>
                <Plus className="h-3.5 w-3.5" /> Change
              </Button>
            )
          }
        >
          {adding === "change" && (
            <div className="mb-4 rounded-md border border-border p-3">
              <ChangeForm endpoint={`/api/clients/${clientId}/payroll/changes`} onDone={() => setAdding(null)} />
            </div>
          )}
          {!data.changes.length ? (
            <p className="text-sm text-muted-foreground">No changes yet. The client can send changes and new hires from the portal.</p>
          ) : (
            <ul className="space-y-3">
              {data.changes.slice(0, 6).map((c) => (
                <ChangeCard key={c.id} c={c} canEdit={canEdit} />
              ))}
              {data.changes.length > 6 && (
                <li className="text-sm">
                  <Link href="/payroll/changes" className="text-primary hover:underline">
                    View all changes
                  </Link>
                </li>
              )}
            </ul>
          )}
        </Section>
      </div>
      <Section title="Filings" description={`From the payroll rules reviewed ${formatDate(data.rulesReviewed)}. Record each one as it's filed; filed copies can be shared with the client.`}>
        <div className="-m-5">
          <FilingList rows={[...data.filings].sort((a, b) => (a.status === "due" ? 0 : 1) - (b.status === "due" ? 0 : 1) || a.dueOn.localeCompare(b.dueOn)).slice(0, 25)} canEdit={canEdit} />
        </div>
        {data.filings.length > 25 && (
          <p className="mt-8 text-sm">
            Showing 25 of {data.filings.length}.{" "}
            <Link href="/payroll/filings" className="text-primary hover:underline">
              View all filings
            </Link>
          </p>
        )}
      </Section>
      <div className="grid gap-6 lg:grid-cols-2">
        {data.famli && (
          <Section title="Maryland FAMLI readiness" description="Contributions start January 1, 2027: 0.9% of wages up to the Social Security wage base; employees can pay up to half. Employers with fewer than 15 employees don't pay the employer half. First report due April 30, 2027.">
            <ul className="space-y-2">
              {data.famli.map((i) => (
                <li key={i.key}>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" disabled={!canEdit} checked={!!i.doneOn} onChange={(e) => famli.mutate({ key: i.key, done: e.target.checked })} />
                    <span className={cn("flex-1", i.doneOn && "text-muted-foreground")}>{i.label}</span>
                    {i.doneOn && <span className="text-xs text-muted-foreground">{formatDate(i.doneOn)}</span>}
                  </label>
                </li>
              ))}
            </ul>
          </Section>
        )}
        <Section title="Payroll settings" description={`${PROVIDERS[data.profile.provider]}${data.profile.providerName ? ` (${data.profile.providerName})` : ""} · ${data.profile.states.join(", ") || "no state"}`}>
          {canEdit ? <ProfileForm clientId={clientId} p={data.profile} depositSchedule={data.depositSchedule} frequencies={data.frequencies} defaultFrom={data.frequencyDefaultFrom} today={data.today} /> : <p className="text-sm text-muted-foreground">Only staff who manage work can change these settings.</p>}
        </Section>
      </div>
    </div>
  );
}
